import { formatDate, MOMENT_FORMATS } from './dateFormat';

const pickPlanExpiresAt = (plan = {}) =>
  plan.expiresAt ??
  plan.expiresOn ??
  plan.expires ??
  plan.endDate ??
  plan.endsAt ??
  plan.validUntil ??
  plan.periodEnd ??
  plan.entitlementEndsAt ??
  plan.expires_at ??
  plan.end_date ??
  plan.ends_at ??
  plan.period_end ??
  plan.entitlement_ends_at ??
  null;

export const formatPartnerPlanExpiresAt = (value) => {
  if (value == null || value === '') return null;
  return formatDate(value, MOMENT_FORMATS.DATE) || String(value).trim() || null;
};

export const normalizePartnerSubscriptionPlan = (plan = {}) => {
  const amount = Number(plan.amount);
  const billingPeriodMonths = Number(plan.billingPeriodMonths);
  const key = String(plan.key ?? plan.pricingId ?? '')
    .trim()
    .toLowerCase();
  const status = String(plan.status ?? '')
    .trim()
    .toLowerCase();
  const expiresAtRaw = pickPlanExpiresAt(plan);
  const startDate = plan.startDate ?? plan.start_date ?? null;

  return {
    subscriptionId: plan.subscriptionId ?? plan.subscription_id ?? null,
    pricingId: plan.pricingId ?? plan.id ?? null,
    planId: plan.planId ?? null,
    key: key || null,
    name: String(plan.name ?? 'Partner Portal').trim() || 'Partner Portal',
    sortOrder: Number.isFinite(Number(plan.sortOrder)) ? Number(plan.sortOrder) : 0,
    amount: Number.isFinite(amount) ? amount : 0,
    currency:
      String(plan.currency ?? 'AED')
        .trim()
        .toUpperCase() || 'AED',
    status: status || null,
    active: plan.active === true || status === 'active' || (plan.active !== false && !status),
    billingPeriodMonths:
      Number.isFinite(billingPeriodMonths) && billingPeriodMonths > 0 ? billingPeriodMonths : 1,
    startDate: startDate || null,
    endDate: plan.endDate ?? plan.end_date ?? null,
    expiresAt: expiresAtRaw || null,
    expiresAtLabel: formatPartnerPlanExpiresAt(expiresAtRaw),
  };
};

export const normalizePartnerPlansResponse = (payload = {}) => {
  const root = payload?.data ?? payload ?? {};
  const plans = (root.subscriptionPlans ?? root.plans ?? [])
    .filter(Boolean)
    .map(normalizePartnerSubscriptionPlan)
    .filter((plan) => Number.isFinite(plan.amount) && plan.amount > 0)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.amount - b.amount);

  const rootExpiresAt = pickPlanExpiresAt(root);
  const currentPlanRaw = root.currentPlan ?? null;
  let currentPlan =
    currentPlanRaw && typeof currentPlanRaw === 'object'
      ? normalizePartnerSubscriptionPlan(currentPlanRaw)
      : currentPlanRaw
        ? normalizePartnerSubscriptionPlan({ key: currentPlanRaw })
        : null;

  if (currentPlan && !currentPlan.expiresAt && rootExpiresAt) {
    currentPlan = {
      ...currentPlan,
      expiresAt: rootExpiresAt,
      expiresAtLabel: formatPartnerPlanExpiresAt(rootExpiresAt),
    };
  }

  return {
    subscriptionPlans: plans,
    currentPlan,
    currentPlanKey: currentPlan?.key ?? currentPlan?.pricingId ?? null,
    currentPricingId: currentPlan?.pricingId ?? null,
  };
};

export const pickPartnerCheckoutUrl = (payload = {}) => {
  const root = payload?.data ?? payload ?? {};
  const payment = root.payment ?? {};
  const candidates = [
    payment.redirectUrl,
    payment.checkoutUrl,
    payment.paymentUrl,
    payment.url,
    root.checkoutUrl,
    root.redirectUrl,
    root.paymentUrl,
    root.url,
    root.link,
    root.sessionUrl,
    root?.checkout?.url,
    root?.session?.url,
  ];

  for (const value of candidates) {
    const url = String(value ?? '').trim();
    if (url) return url;
  }

  return null;
};

/** Single partner-portal fee presentation (no basic/premium/pro tiers). */
export const PARTNER_PORTAL_FEE_PRESENTATION = {
  description: 'Monthly partner portal access for your organization.',
  features: ['Partner portal access', 'Event management tools', 'Community + guest tools'],
};

export const getPartnerSubscriptionPlanPresentation = () => PARTNER_PORTAL_FEE_PRESENTATION;

export const formatPartnerPlanAmount = (amount) => {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '—';
  return new Intl.NumberFormat('en-US').format(value);
};

export const PARTNER_SUBSCRIPTION_PATH = '/partner-subscription';

export const buildPartnerPlanCheckoutReturnUrls = () => {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const base = `${origin}${PARTNER_SUBSCRIPTION_PATH}`;
  return {
    successUrl: `${base}?checkout=success`,
    cancelUrl: `${base}?checkout=cancel`,
    failureUrl: `${base}?checkout=failure`,
  };
};
